import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, withModifiers as _withModifiers, withKeys as _withKeys } from "vue"

const _hoisted_1 = ["onKeydown"]

import { computed, reactive, ref, watch } from 'vue';
import RecursiveSelectorNode from './RecursiveSelectorNode.vue';
import { VariableType } from '@/models/etc/types';
import {
  buildRecursiveSearchIndex,
  searchInRecursiveIndex,
} from './composables/useRecursiveSearchMap';
import { flattenRecursiveTree } from './composables/useRecursiveTreeState';


export default /*@__PURE__*/_defineComponent({
  __name: 'RecursiveSelector',
  props: {
    viewport: { default: undefined },
    elements: { default: undefined },
    modelValue: { default: () => [] },
    takeIdsOnly: { type: Boolean, default: false },
    allowMultiple: { type: Boolean, default: false },
    hideCheckboxes: { type: Boolean, default: false },
    showRecords: { type: Boolean, default: true },
    isColumn: { type: Boolean, default: false },
    parameterTypeName: { default: 'valueType' },
    returnType: { default: 'single-element' },
    elementType: { default: VariableType.Entity },
    nestedKeyName: { default: '' },
    entityInsideName: { default: '' },
    filterString: { default: '' },
    openOnStart: { type: Boolean, default: false },
    valueType: { default: 0 },
    filterOutList: { default: () => [] }
  },
  emits: ["update:modelValue", "elementToggled"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

__expose({ trackArrowUp, trackArrowDown, toggleCurrent, selectCurrent });

const viewportInner = ref<HTMLElement | null>(null);
const rowElements = reactive<Record<string, HTMLElement | null>>({});
const nodeRefs = reactive<Record<string, any>>({});
const expandedKeys = ref<Set<string>>(new Set<string>());
const searchCollapsedKeys = ref<Set<string>>(new Set<string>());
const seededOnce = ref(false);
const indexSelected = ref(-1);
let timeout: ReturnType<typeof setTimeout> | undefined;

const sourceNodes = computed<any[]>(() => (
  props.returnType === 'none' || !props.elements
    ? (props.modelValue || [])
    : (props.elements || [])
));

const theModel = computed<any[]>({
  get: () => props.modelValue || [],
  set: (value) => emit('update:modelValue', value),
});

function getChildren(node: any): any[] {
  const key = props.nestedKeyName || '';
  if (!key) return [];
  const children = node?.[key];
  return Array.isArray(children) ? children : [];
}

function keySeed(node: any) {
  if (props.isColumn && Array.isArray(node?.IdChain)) return node.IdChain.join('_');
  if (node?.Id !== undefined && node?.Id !== null) return String(node.Id);
  if (props.entityInsideName && node?.[props.entityInsideName]?.Id !== undefined) {
    return `${props.entityInsideName}_${node[props.entityInsideName].Id}`;
  }
  if (node?.Name) return String(node.Name);
  return '';
}

function getNodeKey(node: any, parentKey: string | null, index: number): string {
  // Path-based key: parent path + sibling index + seed.
  // This prevents collisions when sibling nodes share the same Id/Name.
  const seed = keySeed(node) || 'node';
  const levelKey = `i${index}::${seed}`;
  return parentKey ? `${parentKey}::${levelKey}` : `root::${levelKey}`;
}

function getModelElement(node: any): any {
  if (props.isColumn) return node?.IdChain;
  if (props.elementType === VariableType.Entity && props.takeIdsOnly) return node?.Id;
  if (props.elementType === VariableType.EntityInside) {
    return node?.[props.entityInsideName || '']?.Id ?? node;
  }
  return node;
}

function appendToChain(chain: any[], node: any): any[] {
  if (props.returnType !== 'chain') return [];
  return [...chain, getModelElement(node)];
}

function getElementName(node: any): string {
  if (!node) return '';
  if (props.elementType === VariableType.EntityInside && props.entityInsideName) {
    return node?.[props.entityInsideName]?.Name ?? '';
  }
  if (props.elementType === VariableType.Entity) {
    return node?.[props.isColumn ? 'compound_name' : 'Name'] ?? '';
  }
  if (props.elementType === VariableType.Boolean) {
    return node ? 'true' : 'false';
  }
  return `${node?.Name ?? node ?? ''}`;
}

const searchIndex = computed(() => buildRecursiveSearchIndex({
  roots: sourceNodes.value,
  getChildren,
  getKey: getNodeKey,
  getText: getElementName,
}));

const searchResult = computed(() => searchInRecursiveIndex(searchIndex.value, props.filterString || ''));

const effectiveAutoExpandedKeys = computed(() => {
  if (!searchResult.value.searchActive) return new Set<string>();
  const next = new Set(searchResult.value.autoExpandedKeys);
  searchCollapsedKeys.value.forEach((key) => next.delete(key));
  return next;
});

function shouldIncludeNode(node: any): boolean {
  if (!props.filterOutList?.length || !props.parameterTypeName) return true;
  // Исключаем узлы запрещённых типов, чтобы поведение совпадало с legacy selector.
  const nodeTypeValue = node?.[props.parameterTypeName];
  return !props.filterOutList.includes(nodeTypeValue);
}

const flatRows = computed(() => flattenRecursiveTree({
  roots: sourceNodes.value,
  index: searchIndex.value,
  expandedKeys: expandedKeys.value,
  autoExpandedKeys: effectiveAutoExpandedKeys.value,
  searchActive: searchResult.value.searchActive,
  visibleKeys: searchResult.value.visibleKeys,
  getChildren,
  getKey: getNodeKey,
  appendToChain,
  shouldIncludeNode,
}));

function isExpanded(key: string) {
  const autoExpanded = searchResult.value.searchActive && effectiveAutoExpandedKeys.value.has(key);
  return expandedKeys.value.has(key) || autoExpanded;
}

/**
 * Собирает ключи всех узлов с детьми (для seed-раскрытия `openOnStart`).
 * Логика обхода полностью совпадает с flattenRecursiveTree, но без учёта поиска
 * и shouldIncludeNode — на инициализации они либо ещё не активны, либо не мешают
 * иметь ключ в expandedKeys (исключённые узлы просто не попадут в flatRows).
 */
function collectAllExpandableKeys(roots: any[]): Set<string> {
  const result = new Set<string>();
  const visit = (nodes: any[], parentKey: string | null) => {
    nodes.forEach((node, index) => {
      const children = getChildren(node) || [];
      if (children.length > 0) {
        const key = getNodeKey(node, parentKey, index);
        result.add(key);
        visit(children, key);
      }
    });
  };
  visit(roots || [], null);
  return result;
}

// One-shot seed: при первом непустом sourceNodes (и openOnStart=true) разворачиваем все узлы.
// После этого пользователь может сворачивать/разворачивать ветки руками.
// Безопасность от бесконечных recursive updates:
//   - геттер возвращает boolean и зависит только от props.openOnStart, seededOnce, длины sourceNodes
//   - watcher не deep, мутация expandedKeys не затрагивает геттер
//   - после успешного seed вызываем stop() (страховка поверх seededOnce)
//   - flush:'post' — мутация уходит в следующий tick
//
// Pattern `let stopFn = null; stopFn = watch(...)` нужен, потому что с `immediate: true`
// callback может выполниться до того, как watch() успеет вернуть значение —
// при `const` это даёт TDZ-ошибку «Cannot access ... before initialization».
let stopOpenOnStartWatcher: (() => void) | null = null;
stopOpenOnStartWatcher = watch(
  () => props.openOnStart && !seededOnce.value && (sourceNodes.value?.length ?? 0) > 0,
  (ready) => {
    if (!ready) return;
    expandedKeys.value = collectAllExpandableKeys(sourceNodes.value);
    seededOnce.value = true;
    if (stopOpenOnStartWatcher) stopOpenOnStartWatcher();
  },
  { immediate: true, flush: 'post' },
);

function onToggleExpand(key: string) {
  // In search mode, auto-expanded nodes should be collapsible manually.
  if (
    searchResult.value.searchActive
    && searchResult.value.autoExpandedKeys.has(key)
    && !expandedKeys.value.has(key)
    && !searchCollapsedKeys.value.has(key)
  ) {
    searchCollapsedKeys.value.add(key);
    searchCollapsedKeys.value = new Set(searchCollapsedKeys.value);
    return;
  }

  // Next click restores default auto-expand behavior for this key.
  if (searchCollapsedKeys.value.has(key)) {
    searchCollapsedKeys.value.delete(key);
    searchCollapsedKeys.value = new Set(searchCollapsedKeys.value);
    return;
  }

  if (expandedKeys.value.has(key)) {
    expandedKeys.value.delete(key);
  } else {
    expandedKeys.value.add(key);
  }
  expandedKeys.value = new Set(expandedKeys.value);
}

watch(
  () => searchResult.value.searchActive,
  (active) => {
    if (!active && searchCollapsedKeys.value.size) {
      searchCollapsedKeys.value = new Set<string>();
    }
  },
);

watch(
  () => props.filterString,
  () => {
    if (searchCollapsedKeys.value.size) {
      searchCollapsedKeys.value = new Set<string>();
    }
  },
);

function onRowMounted(payload: { key: string; el: HTMLElement | null }) {
  rowElements[payload.key] = payload.el;
}

function resetSelectionTimer() {
  if (timeout) clearTimeout(timeout);
  timeout = setTimeout(() => {
    indexSelected.value = -1;
    timeout = undefined;
  }, 2000);
}

function scrollToCurrent() {
  const row = flatRows.value[indexSelected.value];
  if (!row) return;
  const el = rowElements[row.key];
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function trackArrowUp(step: number, resetIndex = false) {
  if (!flatRows.value.length) return;
  if (resetIndex) indexSelected.value = -1;

  if (indexSelected.value < 0) {
    indexSelected.value = flatRows.value.length - 1;
  } else {
    indexSelected.value = Math.max(0, indexSelected.value - step);
  }

  scrollToCurrent();
  resetSelectionTimer();
}

function trackArrowDown(step: number, resetIndex = false) {
  if (!flatRows.value.length) return;
  if (resetIndex) indexSelected.value = -1;

  if (indexSelected.value < 0) {
    indexSelected.value = 0;
  } else {
    indexSelected.value = Math.min(flatRows.value.length - 1, indexSelected.value + step);
  }

  scrollToCurrent();
  resetSelectionTimer();
}

function toggleCurrent() {
  const row = flatRows.value[indexSelected.value];
  if (!row) return;
  const refNode = nodeRefs[row.key];
  refNode && refNode.toggle && refNode.toggle();
}

function selectCurrent() {
  toggleCurrent();
}

function elementToggled(index: number) {
  indexSelected.value = index;
  emit('elementToggled', true);
  resetSelectionTimer();
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "viewportInner",
    ref: viewportInner,
    tabindex: "0",
    class: "rec-ref no-outline",
    onKeydown: [
      _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (trackArrowDown(1)), ["exact","prevent"]), ["down"])),
      _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (trackArrowUp(1)), ["exact","prevent"]), ["up"])),
      _withKeys(_withModifiers(toggleCurrent, ["prevent"]), ["enter"])
    ]
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(flatRows.value, (row, index) => {
      return (_openBlock(), _createBlock(RecursiveSelectorNode, {
        key: row.key,
        ref_for: true,
        ref: (el) => { nodeRefs[row.key] = el; },
        node: row.node,
        "row-key": row.key,
        depth: row.depth,
        "has-children": row.hasChildren,
        "has-raw-children": row.hasRawChildren,
        expanded: isExpanded(row.key),
        active: indexSelected.value === index,
        label: getElementName(row.node),
        tokens: searchResult.value.tokens,
        "allow-multiple": _ctx.allowMultiple,
        "take-ids-only": _ctx.takeIdsOnly,
        "hide-checkboxes": _ctx.hideCheckboxes,
        "show-records": _ctx.showRecords,
        "is-column": _ctx.isColumn,
        "return-type": _ctx.returnType,
        "element-type": _ctx.elementType,
        "nested-key-name": _ctx.nestedKeyName,
        "entity-inside-name": _ctx.entityInsideName,
        chain: row.chain,
        modelValue: theModel.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((theModel).value = $event)),
        onToggleExpand: onToggleExpand,
        onElementToggled: ($event: any) => (elementToggled(index)),
        onRowMounted: onRowMounted
      }, null, 8, ["node", "row-key", "depth", "has-children", "has-raw-children", "expanded", "active", "label", "tokens", "allow-multiple", "take-ids-only", "hide-checkboxes", "show-records", "is-column", "return-type", "element-type", "nested-key-name", "entity-inside-name", "chain", "modelValue", "onElementToggled"]))
    }), 128))
  ], 40, _hoisted_1))
}
}

})